import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { listBills } from "../../lib/api-modules/bills.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { listSpendingTransactions } from "../../lib/api-modules/spending-transactions.js";
import { savingsRate } from "../../lib/util/fire.js";
import { Card } from "../design-system/index.js";

/**
 * Mr. Money Mustache "Shockingly Simple Math behind Early Retirement"
 * table (2012). Maps savings-rate-as-percent-of-take-home-pay to years
 * until financial independence, assuming:
 *
 *   - 5% real return (S&P historical)
 *   - 4% withdrawal rate (Trinity)
 *   - Starting from $0
 *   - Spend = (1 - savings_rate) × take-home, indefinitely
 *
 * Phase 6 Session 1. Renders below the FireTile on /dashboard. The
 * user's current savings-rate row is highlighted with the accent-soft
 * background so the spouse sees "we're here on the chart."
 *
 * **Why hardcode the numbers** rather than re-derive each render: the
 * Mustachian table is the canonical artifact the household has seen
 * online — matching it byte-for-byte makes the numbers feel
 * authoritative. The math is also independent of every Hearth setting
 * (it assumes 4% / 5%, not the user's withdrawal/return overrides) —
 * pulling from `timeToFI` would couple the table to per-household
 * assumptions and break the "compare yourself to the canonical" framing.
 */

interface MmmRow {
  readonly rate_pct: number;
  /** Display string — sometimes a fractional year (e.g. "14.5") or a
   *  range ("Under 3"). */
  readonly years_label: string;
  /** Numeric center of the bucket for sort/match — "Under 3" → 2. */
  readonly years_numeric: number;
}

// Mr. Money Mustache "Shockingly Simple Math" — 5% real, 4% withdrawal,
// starting from $0. The table goes by 5-percentage-point steps; our
// row-highlight bucket-rounds to the nearest 5%.
const MMM_ROWS: readonly MmmRow[] = [
  { rate_pct: 5, years_label: "66", years_numeric: 66 },
  { rate_pct: 10, years_label: "51", years_numeric: 51 },
  { rate_pct: 15, years_label: "43", years_numeric: 43 },
  { rate_pct: 20, years_label: "37", years_numeric: 37 },
  { rate_pct: 25, years_label: "32", years_numeric: 32 },
  { rate_pct: 30, years_label: "28", years_numeric: 28 },
  { rate_pct: 35, years_label: "25", years_numeric: 25 },
  { rate_pct: 40, years_label: "22", years_numeric: 22 },
  { rate_pct: 45, years_label: "19", years_numeric: 19 },
  { rate_pct: 50, years_label: "17", years_numeric: 17 },
  { rate_pct: 55, years_label: "14.5", years_numeric: 14.5 },
  { rate_pct: 60, years_label: "12.5", years_numeric: 12.5 },
  { rate_pct: 65, years_label: "10.5", years_numeric: 10.5 },
  { rate_pct: 70, years_label: "8.5", years_numeric: 8.5 },
  { rate_pct: 75, years_label: "7", years_numeric: 7 },
  { rate_pct: 80, years_label: "5.5", years_numeric: 5.5 },
  { rate_pct: 85, years_label: "4", years_numeric: 4 },
  { rate_pct: 90, years_label: "Under 3", years_numeric: 2.5 },
  { rate_pct: 95, years_label: "Under 2", years_numeric: 1.5 },
];

/**
 * Self-contained: fetches bills + income + spending and computes the
 * trailing-12 savings rate via the same `savingsRate` helper as the
 * FireTile. TanStack Query keys are identical, so the two components
 * share a single network request per query.
 */
export function ShockinglySimpleMathTable() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const trailingFrom = useMemo(() => {
    const d = new Date(today);
    d.setUTCMonth(d.getUTCMonth() - 12);
    return d.toISOString().slice(0, 10);
  }, [today]);
  const billsQuery = useQuery({
    queryKey: ["bills", { includeArchived: false }],
    queryFn: () => listBills(false),
  });
  const incomeQuery = useQuery({
    queryKey: ["income-events", { from: trailingFrom }],
    queryFn: () => listIncomeEvents({ from: trailingFrom }),
  });
  const spendQuery = useQuery({
    queryKey: ["spending-transactions", { from: trailingFrom, to: today }],
    queryFn: () =>
      listSpendingTransactions({ from: trailingFrom, to: today }),
  });

  const ratePct = useMemo(() => {
    if (
      billsQuery.isLoading ||
      incomeQuery.isLoading ||
      spendQuery.isLoading ||
      billsQuery.isError ||
      incomeQuery.isError ||
      spendQuery.isError
    ) {
      return null;
    }
    const r = savingsRate({
      bills: billsQuery.data?.bills ?? [],
      income_events: incomeQuery.data?.income_events ?? [],
      spending_transactions: spendQuery.data?.spending_transactions ?? [],
      today,
      lookback_months: 12,
    });
    if (r.gross_income_cents === 0) return null;
    return r.savings_rate_pct;
  }, [billsQuery, incomeQuery, spendQuery, today]);

  const highlightRate = ratePct === null ? null : nearestBucket(ratePct);

  return (
    <Card className="mb-6">
      <div className="mb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Shockingly Simple Math
        </p>
        <p className="mt-0.5 text-sm text-[var(--color-muted)]">
          Mr. Money Mustache (2012): savings rate → years to FI, assuming
          5% real return + 4% withdrawal + starting from zero. Independent
          of your current net worth.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse text-sm"
          data-testid="shockingly-simple-math-table"
        >
          <thead>
            <tr className="border-b border-[var(--color-border-default)] text-left">
              <th className="py-2 pr-4 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
                Savings rate
              </th>
              <th className="py-2 pr-4 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
                Years to FI
              </th>
              <th className="py-2 pr-4 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
                Spend ratio
              </th>
            </tr>
          </thead>
          <tbody>
            {MMM_ROWS.map((row) => {
              const highlight = highlightRate === row.rate_pct;
              return (
                <tr
                  key={row.rate_pct}
                  className={
                    highlight
                      ? "border-b border-[var(--color-border-subtle)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
                      : "border-b border-[var(--color-border-subtle)] text-[var(--color-text)]"
                  }
                  data-testid={`mmm-row-${row.rate_pct}`}
                  data-highlighted={highlight ? "true" : undefined}
                >
                  <td className="py-2 pr-4 font-mono tabular-nums">
                    {row.rate_pct}%
                    {highlight && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide">
                        ← you
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-4 font-mono tabular-nums">
                    {row.years_label}
                  </td>
                  <td className="py-2 pr-4 font-mono tabular-nums text-[var(--color-muted)]">
                    {(100 - row.rate_pct) / 100 === 0
                      ? "0"
                      : ((100 - row.rate_pct) / row.rate_pct).toFixed(1)}
                    × earn
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * Round savings_rate_pct to the nearest 5% bucket within [5, 95].
 * Returns null when rate is outside the table range.
 */
function nearestBucket(ratePct: number): number | null {
  if (ratePct < 2.5 || ratePct >= 97.5) return null;
  const bucket = Math.round(ratePct / 5) * 5;
  if (bucket < 5) return 5;
  if (bucket > 95) return 95;
  return bucket;
}
